import { Email, EmailChangeCallbackResult, EmailChangeCallbackStep, isNilOrEmpty } from '@rentguru/commons-utils';
import { ToastVariant } from '@up2rent/ui-new';
import isEqual from 'lodash/isEqual';

import RouteDestination from 'src/components/Routes/RouteDestination';
import SettingsRouteDestination from 'src/components/Settings/SettingsRouteDestination';
import { IMPERSONATION_BLOCKED_CODE, IMPERSONATION_BLOCKED_MESSAGE_ID } from 'src/utils/impersonationUtils';

const EMAIL_ALREADY_USED_CODE = 'EMAIL_ALREADY_USED';
const SAME_EMAIL_MESSAGE = 'Email is the same';
const LINK_OTHER_ACCOUNT_CODE = 'INVALID_USER';
const LINK_EXPIRED_CODE = 'TOKEN_EXPIRED';

const { SECURITY_LOGIN_ROUTE } = SettingsRouteDestination;

export const EMAIL_CHANGE_CALLBACK_PATH = `${RouteDestination.SETTINGS}/${SECURITY_LOGIN_ROUTE}`;

export interface EmailChangeCallbackFeedback {
  messageId: string;
  variant: ToastVariant;
}

export const getEmailChangeErrorMessageId = (message?: string | null): string => {
  switch (message) {
    case EMAIL_ALREADY_USED_CODE:
      return 'error.UsernameExistsException';
    case SAME_EMAIL_MESSAGE:
      return 'settings.security.emailChangeSameAddress';
    case IMPERSONATION_BLOCKED_CODE:
      return IMPERSONATION_BLOCKED_MESSAGE_ID;
    default:
      return 'settings.security.emailChangeError';
  }
};

const getEmailChangeLinkErrorMessageId = (errorCode: string): string => {
  switch (errorCode) {
    case LINK_OTHER_ACCOUNT_CODE:
      return 'settings.security.emailChangeLinkOtherAccount';
    case LINK_EXPIRED_CODE:
      return 'settings.security.emailChangeLinkExpired';
    default:
      return 'settings.security.emailChangeLinkInvalid';
  }
};

export const getEmailChangeCallbackFeedback = ({
  step,
  errorCode,
}: EmailChangeCallbackResult): EmailChangeCallbackFeedback => {
  if (!isNilOrEmpty(errorCode)) {
    return { messageId: getEmailChangeLinkErrorMessageId(errorCode), variant: ToastVariant.ERROR };
  }
  switch (step) {
    case EmailChangeCallbackStep.VERIFIED:
      return { messageId: 'settings.security.emailChangeCompleted', variant: ToastVariant.SUCCESS };
    case EmailChangeCallbackStep.CONFIRMED:
    default:
      return { messageId: 'settings.security.emailChangeApproved', variant: ToastVariant.SUCCESS };
  }
};

export const isSameEmailAddress = (left?: string | null, right?: string | null): boolean =>
  !isNilOrEmpty(left) && !isNilOrEmpty(right) && left.trim().toLowerCase() === right.trim().toLowerCase();

/**
 * Puts `primaryAddress` back on the primary entry. An address the user promoted to primary that was already on the
 * contact (`existingAddresses`) is kept as a secondary entry, so an abandoned change does not delete it.
 */
export const keepPrimaryEmailAddress = (
  emails: Email[],
  primaryAddress?: string | null,
  existingAddresses: string[] = []
): Email[] => {
  if (isNilOrEmpty(primaryAddress)) {
    return emails.filter((entry) => !entry.isPrimary);
  }
  const wasOnContact = (address: string) => existingAddresses.some((existing) => isSameEmailAddress(existing, address));
  const primaryLabel = emails.find(
    (entry) => !entry.isPrimary && isSameEmailAddress(entry.address, primaryAddress)
  )?.label;
  return emails.reduce((acc: Email[], entry) => {
    if (entry.isPrimary) {
      acc.push({ ...entry, address: primaryAddress, label: primaryLabel === undefined ? entry.label : primaryLabel });
      if (!isSameEmailAddress(entry.address, primaryAddress) && wasOnContact(entry.address)) {
        acc.push({ ...entry, isPrimary: false });
      }
      return acc;
    }
    if (!isSameEmailAddress(entry.address, primaryAddress)) {
      acc.push(entry);
    }
    return acc;
  }, []);
};

export interface ContactEmailFieldsInput {
  email?: string | null;
  emails: Email[];
  originalEmail?: string | null;
  originalEmails?: Email[] | null;
  ownAccountEmail?: string | null;
  canStartEmailChange: boolean;
}

export interface ContactEmailFieldsToSave {
  shouldUpdate: boolean;
  email?: string;
  emails: Email[];
  requestedEmail?: string;
  startsOwnEmailChange: boolean;
}

const getPrimaryAddress = (emails?: Email[] | null): string | undefined =>
  emails?.find((entry) => entry.isPrimary && !isNilOrEmpty(entry.address))?.address;

/**
 * The primary entry of `emails` is what the user sees and edits, and the contact validator requires the top-level
 * `email` to equal it. While a member's own change waits for verification, both stay on the account email.
 */
export const getContactEmailFieldsToSave = ({
  email,
  emails,
  originalEmail,
  originalEmails,
  ownAccountEmail,
  canStartEmailChange,
}: ContactEmailFieldsInput): ContactEmailFieldsToSave => {
  const filledEmails = emails.filter((entry) => !isNilOrEmpty(entry.address));
  const fallbackEmail = isNilOrEmpty(email) ? undefined : email;
  const requestedEmail = getPrimaryAddress(filledEmails) ?? fallbackEmail;
  const originalRequestedEmail = getPrimaryAddress(originalEmails) ?? originalEmail ?? undefined;
  const isEmailChanged = !isNilOrEmpty(requestedEmail) && !isSameEmailAddress(requestedEmail, originalRequestedEmail);
  const startsOwnEmailChange =
    canStartEmailChange &&
    isEmailChanged &&
    !isNilOrEmpty(ownAccountEmail) &&
    !isSameEmailAddress(requestedEmail, ownAccountEmail);
  const emailsToSave =
    startsOwnEmailChange && !isNilOrEmpty(ownAccountEmail)
      ? keepPrimaryEmailAddress(
          filledEmails,
          ownAccountEmail,
          (originalEmails ?? []).map((entry) => entry.address)
        )
      : filledEmails;
  const emailToSave = startsOwnEmailChange
    ? (ownAccountEmail ?? undefined)
    : (getPrimaryAddress(emailsToSave) ?? fallbackEmail);
  const listChanged = !isEqual(emailsToSave, originalEmails ?? []);
  const topLevelChanged = (emailToSave ?? null) !== (originalEmail ?? null);
  return {
    shouldUpdate: listChanged || topLevelChanged,
    email: emailToSave,
    emails: emailsToSave,
    requestedEmail,
    startsOwnEmailChange,
  };
};
